import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { FileText, Filter, Calendar, User, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogsPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    action_type: '',
    entity_type: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    action_types: [],
    entity_types: [],
    users: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const [typesRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/api/staff-admin/audit-logs/action-types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/staff-admin/audit-logs/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (typesRes.ok) {
        const typesData = await typesRes.json();
        setFilterOptions(prev => ({
          ...prev,
          action_types: typesData.action_types || [],
          entity_types: typesData.entity_types || []
        }));
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setFilterOptions(prev => ({
          ...prev,
          users: usersData.users || []
        }));
      }
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);

      const response = await fetch(`${API_URL}/api/staff-admin/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();
      setLogs(data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        pages: data.pages
      }));
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  const formatAction = (action) => {
    return action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
  };

  const formatDetails = (details, action) => {
    if (!details) return '-';
    if (typeof details === 'string') return details;
    
    const keys = Object.keys(details);
    if (keys.length === 0) return '-';
    
    // Handle EMAIL_NOTIFICATION_SENT events
    if (action === 'EMAIL_NOTIFICATION_SENT') {
      const type = details.notification_type || 'email';
      const recipient = details.recipient || (details.recipients || []).join(', ');
      const enabled = details.email_enabled;
      const status = enabled ? 'SENT' : 'DISABLED';
      return `${type.replace(/_/g, ' ')} → ${recipient} [${status}]`;
    }
    
    // Handle approval events
    if (action?.includes('APPROVED') || action?.includes('REJECTED') || action?.includes('RESET')) {
      const actor = details.actor_email || '';
      const applicant = details.applicant_name || details.applicant_email || '';
      const status = details.final_status || '';
      if (actor && applicant) {
        return `${actor} → ${applicant}${status ? ` (${status})` : ''}`;
      }
    }
    
    // Show changes if present
    if (details.changes) {
      return Object.entries(details.changes)
        .map(([field, change]) => `${field}: ${change.old || 'null'} → ${change.new || 'null'}`)
        .join(', ');
    }
    
    if (details.fields_changed) {
      return `Changed: ${details.fields_changed.join(', ')}`;
    }
    
    // Default: show first few key-value pairs
    return keys.slice(0, 3).map(k => `${k}: ${JSON.stringify(details[k]).slice(0, 30)}`).join(', ');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      user_id: '',
      action_type: '',
      entity_type: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'Audit Logs' }
          ]}
        />

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-600" />
            Audit Logs
          </h1>
          <p className="text-gray-600 mt-1">
            Read-only view of system changes for compliance and oversight.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="end-date-filter"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">User</label>
              <select
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="user-filter"
              >
                <option value="">All Users</option>
                {filterOptions.users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Action Type</label>
              <select
                value={filters.action_type}
                onChange={(e) => handleFilterChange('action_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="action-filter"
              >
                <option value="">All Actions</option>
                {filterOptions.action_types.map(a => (
                  <option key={a} value={a}>{formatAction(a)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Entity Type</label>
              <select
                value={filters.entity_type}
                onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="entity-filter"
              >
                <option value="">All Entities</option>
                {filterOptions.entity_types.map(e => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No audit logs found matching your filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entity Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entity ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Summary
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log, idx) => (
                    <tr key={log.id || idx} className="hover:bg-gray-50" data-testid={`audit-log-row-${idx}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{log.actor_name || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">{log.actor_email}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <span className="capitalize">{log.resource_type || '-'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                        <span className="truncate block max-w-[120px]" title={log.resource_id || '-'}>
                          {log.resource_id ? log.resource_id.slice(0, 12) + '...' : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={JSON.stringify(log.details)}>
                        {formatDetails(log.details, log.action)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell font-mono">
                        {log.ip_address || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogsPage;
